// One ledger, two surfaces.
//
// The panel draws it as a seven-column grid and the board as two-line cards,
// but what those two are drawing has to be the same thing: the same rows, in the
// same groups, carrying the same figures. So the model is built here, once, and
// each surface only decides how to paint it.
//
// `MUQ_FORMAT.ledger` in `extension-mu/format.js` IS this function: that file is
// built from this module (`scripts/build-mu-format.mjs`). It used to be a
// hand-written mirror kept in step by a parity test, which is how the two
// surfaces drifted in everything the test did not name.
//
// Two rules the ledger exists to keep:
//
//   The rows and the bar report the same quantity. Both are `promised - dealt`
//   (spec invariant 1). A row that printed the whole promise while the bar
//   printed what was left cost two review rounds.
//
//   Everything gear knows is context. The damage figures already include every
//   contribution gear makes, and no per-set cap exists anywhere in the game, so
//   the strip says who is equipped for what and never states a damage rule.

import type { MuGearSlot, MuRosterMember, MuState } from "@shared/types";
import { debuffLabel, pillLabel, type Effect } from "./effects";

/** What the payout column is a claim about, which is not the same in every
 * phase. `none` is a unit holding nothing: no contract, so no claim. */
export type LedgerPhase = "none" | "open" | "bid" | "fulfilling" | "lost" | "settled";

export interface LedgerRow {
  userId: string;
  username: string;
  avatarUrl: string | null;
  /** Their level, or null when the game did not say. A token beside the face,
   * never a damage claim. */
  level: number | null;
  online: boolean;
  /** False for somebody in the queue the roster no longer carries: they left
   * the unit, or the game refused them. Their figures still stand. */
  known: boolean;
  inQueue: boolean;
  alreadyHitThisRound: boolean;
  isMe: boolean;
  pill: Effect | null;
  penalty: Effect | null;
  /** The pill's own percentage, kept as a number so sorting does not have to
   * parse the words back out of the chip. Zero when there is no pill. */
  pillPercent: number;
  /** What they are wearing, or null when the lookup failed. Null is "we do not
   * know", which is not the same as an unequipped fighter. */
  gear: MuGearSlot[] | null;
  /** What this member is good for: the figure they typed, or the projection
   * standing in for it. */
  committed: number;
  /** True when the figure is one somebody typed rather than one recomputed from
   * health every poll. The hand-set dot marks it. */
  handSet: boolean;
  dealt: number;
  /** The editable figure. Same quantity as the bar (invariant 1). */
  leftToDeal: number;
  /** Their share of what the contract asked for, as a percentage. Zero-target
   * contracts give zero rather than an infinity. */
  sharePct: number;
  /** What they still owe, on the same scale. Certainty is material: the dealt
   * part is solid because it happened, this part is hatched because it was
   * promised, and for a member outside the queue it is dashed because it is
   * merely possible. */
  owedPct: number;
  /** What the game says this contract pays them, when a relay has said so. */
  payout: number | null;
  /**
   * Why this member's figure is what it is: health now, hits once food is
   * counted, and the food left to buy them.
   *
   * The seven columns have no room for it and the roster screen that used to
   * carry it is gone, so it travels with the row and the surfaces put it on the
   * member cell. Hunger rather than the health bar is what really bounds a
   * contract, and a figure bigger than the bar allows looks like a bug without
   * this.
   */
  capacity: { health: number; maxHealth: number; hits: number; hunger: number; maxHunger: number };
}

export interface LedgerGroup {
  id: "in" | "out";
  rows: LedgerRow[];
  count: number;
  committed: number;
  dealt: number;
  leftToDeal: number;
}

export interface Ledger {
  groups: LedgerGroup[];
  /** True before the award, when every dealt figure is zero and the queue is
   * capacity rather than a debt. The last column is headed `committed` then,
   * because there is nothing dealt for anything to be left of. */
  preAward: boolean;
  /** The whole-unit row under the groups: everybody, queued or not. */
  unit: { count: number; committed: number; dealt: number; leftToDeal: number };
  phase: LedgerPhase;
  /** What the contract asked for, or the hand-set target standing in for it.
   * Zero when the unit is judging itself against nothing. */
  target: number;
}

export type SortKey = "member" | "pill" | "dealt" | "left";

/** Which way a sort runs. Names read ascending first and figures descending,
 * because that is the order each is asked for: who is this, and who has most. */
export type SortDir = "asc" | "desc";

export const defaultDir = (key: SortKey | "committed"): SortDir =>
  key === "member" ? "asc" : "desc";

/**
 * Which phase the payout column is describing.
 *
 * Derived from what the unit holds rather than from any one auction: the ledger
 * is about this contract, and a unit with no contract has no claim on anybody's
 * money at all.
 */
export function ledgerPhase(state: Pick<MuState, "contract" | "progress">): LedgerPhase {
  if (!state.contract) return "none";
  if (!state.contract.confirmed) return "none";
  if (state.progress?.reached) return "settled";
  return "fulfilling";
}

function memberRow(
  member: MuRosterMember | undefined,
  fallback: { userId: string; username: string },
  opts: {
    inQueue: boolean;
    committed: number;
    handSet: boolean;
    meId: string | null;
    nowMs: number;
    target: number;
  },
): LedgerRow {
  const dealt = Number(member?.contractDamage) || 0;
  return {
    userId: fallback.userId,
    username: member?.username ?? fallback.username,
    avatarUrl: member?.avatarUrl ?? null,
    level: typeof member?.level === "number" ? member.level : null,
    online: member?.online === true,
    known: Boolean(member),
    inQueue: opts.inQueue,
    alreadyHitThisRound: member?.alreadyHitThisRound === true,
    isMe: opts.meId !== null && fallback.userId === opts.meId,
    pill: member ? pillLabel(member, opts.nowMs) : null,
    penalty: member ? debuffLabel(member, opts.nowMs) : null,
    pillPercent: member ? Number(member.buffPercent) || 0 : 0,
    // Absent because nobody asked, versus absent because the lookup failed:
    // an empty array is a fighter carrying nothing, null is "we cannot say".
    gear: member?.gearKnown === false ? null : (member?.gear ?? []),
    committed: opts.committed,
    handSet: opts.handSet,
    dealt,
    leftToDeal: Math.max(0, opts.committed - dealt),
    sharePct: opts.target > 0 ? (dealt / opts.target) * 100 : 0,
    owedPct:
      opts.target > 0 ? (Math.max(0, opts.committed - dealt) / opts.target) * 100 : 0,
    payout: typeof member?.contractPayout === "number" ? member.contractPayout : null,
    capacity: {
      health: Math.round(Number(member?.currentHealth) || 0),
      maxHealth: Math.round(Number(member?.maxHealth) || 0),
      // hitsCapacity, never floor(health / 10): the base cost is scaled by
      // armour and dodge, and food buys more of the bar.
      hits: Math.round(Number(member?.hitsCapacity) || 0),
      hunger: Math.round(Number(member?.hunger) || 0),
      maxHunger: Math.round(Number(member?.maxHunger) || 0),
    },
  };
}

const totals = (rows: LedgerRow[]) => ({
  count: rows.length,
  committed: rows.reduce((sum, r) => sum + r.committed, 0),
  dealt: rows.reduce((sum, r) => sum + r.dealt, 0),
  leftToDeal: rows.reduce((sum, r) => sum + r.leftToDeal, 0),
});

/**
 * The ledger: everybody in the unit, in the group that says whether they are in.
 *
 * Both groups are always present, including empty ones, because "nobody is in
 * the queue" is a fact the screen has to be able to state and a missing group
 * cannot state anything.
 */
export function ledger(
  state: MuState,
  meId: string | null,
  nowMs: number,
  opts: {
    sort?: SortKey;
    dir?: SortDir;
  } = {},
): Ledger {
  const byId = new Map(state.roster.map((m) => [m.userId, m]));
  const phase = ledgerPhase(state);
  const target = state.contract?.targetDamage ?? state.handTarget ?? 0;

  const queued = [...state.queue]
    .sort((a, b) => a.slot - b.slot)
    .map((q) => {
      const handSet = typeof q.pledgedDamage === "number" && q.pledgedDamage > 0;
      const member = byId.get(q.userId);
      return memberRow(
        member,
        { userId: q.userId, username: q.username },
        {
          inQueue: true,
          committed: handSet ? (q.pledgedDamage as number) : (member?.projectedDamage ?? 0),
          handSet,
          meId,
          nowMs,
          target,
        },
      );
    });

  const inQueue = new Set(queued.map((r) => r.userId));
  const free = state.roster
    .filter((m) => !inQueue.has(m.userId))
    .map((m) =>
      memberRow(
        m,
        { userId: m.userId, username: m.username },
        {
          inQueue: false,
          // Not a promise: what they could bring if asked. The group's own
          // words keep the two apart.
          committed: m.projectedDamage,
          handSet: false,
          meId,
          nowMs,
          target,
        },
      ),
    );

  const groups: LedgerGroup[] = [
    // Queue order is the queue's own answer to "who is next", so it stands
    // until a reader asks for something else. Nobody is waiting in the other
    // group, so there the heaviest lead.
    { id: "in" as const, rows: opts.sort ? sortRows(queued, opts.sort, opts.dir) : queued },
    { id: "out" as const, rows: sortRows(free, opts.sort ?? "committed", opts.dir) },
  ].map(({ id, rows }) => ({
    // The whole group, every time: a unit reads its roster to find one member,
    // and a member behind a button is a member the reader has to guess is there.
    id,
    ...totals(rows),
    rows,
  }));

  return {
    groups,
    preAward: phase === "none" || phase === "open" || phase === "bid",
    // Every member counted once. The two groups are disjoint by construction,
    // so this adds up to what the unit could field rather than to a figure that
    // double-counts anybody.
    unit: totals([...queued, ...free]),
    phase,
    target,
  };
}

/**
 * The order a reader asked for.
 *
 * Every sort falls back to the handle, so a list of members who all dealt
 * nothing has one stable order rather than reshuffling under a tie on every
 * poll. `committed` is not offered as a column head: it is the resting order of
 * the members nobody is waiting on.
 */
export function sortRows(
  rows: LedgerRow[],
  key: SortKey | "committed",
  dir: SortDir = defaultDir(key),
): LedgerRow[] {
  const flip = (list: LedgerRow[]) => (dir === defaultDir(key) ? list : list.reverse());
  // Somebody running a pill is the reason to lean on them now rather than in an
  // hour, so the chips come first and the strongest of them leads.
  const pick: Record<string, (r: LedgerRow) => number> = {
    pill: (r) => r.pillPercent,
    dealt: (r) => r.dealt,
    committed: (r) => r.committed,
    left: (r) => r.leftToDeal,
  };
  if (key === "member") return flip([...rows].sort(byName()));
  return flip([...rows].sort(byName(byNumber(pick[key]))));
}

const byNumber =
  (pick: (r: LedgerRow) => number) =>
  (a: LedgerRow, b: LedgerRow): number =>
    pick(b) - pick(a);

const byName =
  (first?: (a: LedgerRow, b: LedgerRow) => number) =>
  (a: LedgerRow, b: LedgerRow): number =>
    (first ? first(a, b) : 0) || a.username.localeCompare(b.username);

/**
 * How a member's commitment reads when they have not started.
 *
 * `in for X` only while they have dealt nothing (invariant 1): once damage is
 * landing, the figure that matters is what is left, and saying what they took
 * on invites the reader to add the two together.
 */
export function commitmentWords(row: LedgerRow, exact: (n: number) => string): string {
  return row.dealt > 0 ? exact(row.leftToDeal) : `in for ${exact(row.committed)}`;
}

/** Why the figure is what it is, in one sentence for the member cell. Never
 * "health / 10": capacity comes from `hitsCapacity`, and food is what buys it. */
export function capacityWords(row: LedgerRow): string {
  const c = row.capacity;
  return `${c.health} of ${c.maxHealth} health, ${c.hits} hits once food is counted, ${c.hunger} of ${c.maxHunger} food left`;
}

/* ------------------------------------------------------------- the bar */

/**
 * The shape of the coverage bar, on both surfaces.
 *
 * It rescales once the unit is past the target and keeps a marker where the
 * target sits on the new scale. Without that, three times the target drew
 * exactly like scraping in: a bar pinned at a hundred per cent cannot say by
 * how much, and a unit that had massively overshot could not tell that from
 * having just made it. The overshoot is also returned as a figure, because a
 * marker says where the target was and only a number says by how much.
 *
 * The three materials keep their meaning: dealt happened, promised was somebody
 * typing a figure, projected is our arithmetic from their health.
 */
export interface ProgressShape {
  dealtPct: number;
  promisedPct: number;
  estimatedPct: number;
  /** Where the target sits on the current scale, as a percentage. 100 until the
   * unit is past it, at which point the bar has outgrown the target. */
  markerPct: number;
  /** Damage beyond what the contract asks for. Zero when there is none. */
  over: number;
  overshot: boolean;
  /** Nobody has swung yet, which is worth saying rather than leaving an empty
   * solid segment to be read as a missing figure. */
  nothingDealt: boolean;
}

export function progressShape(input: {
  target: number;
  dealt: number;
  promised: number;
  estimated: number;
}): ProgressShape {
  const { target, dealt, promised, estimated } = input;
  const total = dealt + promised + estimated;
  const scale = Math.max(target, total);
  const pct = (value: number) => (scale > 0 ? Math.max(0, (value / scale) * 100) : 0);
  const dealtPct = Math.min(100, pct(dealt));
  const promisedPct = Math.min(100 - dealtPct, pct(promised));
  const estimatedPct = Math.min(100 - dealtPct - promisedPct, pct(estimated));
  return {
    dealtPct,
    promisedPct,
    estimatedPct,
    markerPct: scale > 0 ? Math.min(100, (target / scale) * 100) : 0,
    over: Math.max(0, total - target),
    overshot: total > target,
    nothingDealt: dealt <= 0,
  };
}
